import { useCallback } from 'react';
import { legacy, http } from '@/shared/api/client';
import {
  normalizePointLogs,
  normalizeOptsFromPassbook,
  deriveLeadBalanceFromRegistration,
} from '@/features/leads/model/lead-logs';
import { isCardInstalled, isMultiStoreLeads, isAndroidDevice, isIosDevice } from '@/shared/lib/passbook-utils';
import { toFiniteNumber } from '@/shared/lib/format';
import { normalizeLeadUniqueIdField, normalizeUniqueIdAsString } from '@/shared/lib/unique-id-lookup';
import type { IndividualMessage, Lead, PaginatedResponse, Passbook, PanelUser } from '@/shared/types';
import type { DataTableFetchParams, DataTableFetchResult } from '@/shared/components/data-table';

export interface LeadsStats {
  total: number;
  installed: number;
  notInstalled: number;
  ios: number;
  android: number;
}

/** Payload mínimo que `usersPaginateMulti` usa pra filtrar Operator por loja. */
export type LeadsScopeUser = Pick<PanelUser, 'role' | 'position' | '_id' | 'email' | 'name'>;

export const LEAD_COLUMNS = [
  { data: 'updated' },
  { data: 'createdAt' },
  { data: 'profile.name' },
  { data: 'profile.uniqueId' },
  { data: 'total_points' },
  { data: 'email' },
  { data: 'birthday' },
  { data: 'phone' },
];

function columnIndex(fieldId: string): number {
  const idx = LEAD_COLUMNS.findIndex((c) => c.data === fieldId);
  return idx >= 0 ? idx : 0;
}

function scopeUserPayload(user: LeadsScopeUser | null | undefined) {
  if (!user) return undefined;
  return {
    _id: user._id,
    email: user.email,
    name: user.name,
    role: user.role,
    ...(user.position != null && user.position !== '' ? { position: user.position } : {}),
  };
}

/** Payload do datatable legado — compartilhado com a listagem de auditoria. */
export function buildBody(
  idPassbook: string,
  params: DataTableFetchParams,
  multi: boolean,
  extras?: { onlyBlocked?: boolean },
  scopeUser?: LeadsScopeUser | null,
) {
  const order =
    params.sorting.length > 0
      ? params.sorting.map((s) => ({
          column: columnIndex(s.id),
          dir: s.desc ? ('desc' as const) : ('asc' as const),
        }))
      : [{ column: 0, dir: 'desc' as const }];

  const user = multi ? scopeUserPayload(scopeUser) : undefined;

  return {
    id_passbook: idPassbook,
    role: ['Member', 'Employee'],
    length: params.pageSize,
    skip: params.pageIndex * params.pageSize,
    search: { value: params.search ?? '' },
    order,
    columns: LEAD_COLUMNS,
    ...(multi ? { multiStore: true } : {}),
    ...(user ? { user } : {}),
    ...(params.storeFilter ? { storeFilter: params.storeFilter } : {}),
    ...(params.installedFilter ? { installedFilter: params.installedFilter } : {}),
    ...(params.createdFrom ? { createdFrom: params.createdFrom } : {}),
    ...(params.createdTo ? { createdTo: params.createdTo } : {}),
    ...(extras?.onlyBlocked ? { onlyBlocked: true } : {}),
  };
}

export function useLeadsFetcher(
  idPassbook: string,
  passbook: Passbook,
  options?: { onlyBlocked?: boolean },
  scopeUser?: LeadsScopeUser | null,
) {
  const multi = isMultiStoreLeads(passbook);
  const endpoint = multi ? '/usersPaginateMulti' : '/usersPaginate';
  const onlyBlocked = Boolean(options?.onlyBlocked);

  return useCallback(
    async (params: DataTableFetchParams): Promise<DataTableFetchResult<Lead>> => {
      const res = await legacy.post<PaginatedResponse<Lead>>(
        endpoint,
        buildBody(idPassbook, params, multi, { onlyBlocked }, scopeUser),
        { headers: { 'X-Passbook-Id': idPassbook } },
      );
      return {
        rows: (res.data ?? []).map((row) => {
          const lead = normalizeLeadUniqueIdField(row);
          // Aggregate legado pode devolver total_points como string (Decimal / value / onError).
          const rawPoints = (row as { total_points?: unknown }).total_points;
          if (rawPoints != null && rawPoints !== '') {
            lead.total_points = toFiniteNumber(rawPoints);
          }
          return lead;
        }),
        total: res.recordsFiltered ?? 0,
      };
    },
    [idPassbook, endpoint, multi, onlyBlocked, scopeUser],
  );
}

export async function saveLead(
  idPassbook: string,
  lead: Partial<Lead> & {
    _id: string;
    serialNumber?: string;
    gender?: string;
    observacao?: string;
    description?: string;
  },
) {
  const serialNumber = lead.serialNumber;
  if (!serialNumber) {
    throw new Error('Lead sem serialNumber — não é possível salvar.');
  }

  const uniqueId = normalizeUniqueIdAsString(lead.profile?.uniqueId);
  await legacy.post(
    '/registration/message',
    {
      id_passbook: idPassbook,
      serialNumber,
      uniqueId,
      email: lead.email ?? '',
      phone: lead.phone ?? '',
      birthday: lead.birthday ?? '',
      gender: lead.gender ?? '',
      observacao: lead.observacao ?? '',
      description: lead.description ?? '',
      current_stamps: String(lead.total_points ?? 0),
      count_stamps: String(lead.total_points ?? 0),
    },
    { headers: { 'X-Passbook-Id': idPassbook } },
  );
}

export async function deleteLead(idPassbook: string, leadId: string) {
  await legacy.delete(`/user/${idPassbook}/${leadId}/false`, {
    headers: { 'X-Passbook-Id': idPassbook },
  });
}

export async function resendCard(idPassbook: string, serialNumber: string, email: string) {
  await legacy.post(
    '/mailResend',
    { id_passbook: idPassbook, serial_number: serialNumber, email },
    { headers: { 'X-Passbook-Id': idPassbook } },
  );
}

export async function exportLeads(
  idPassbook: string,
  email: string,
  type: 'users' | 'logs',
  options?: { blocked?: boolean },
) {
  await http.post('/bff/exports/leads', {
    idPassbook,
    email,
    type,
    blocked: Boolean(options?.blocked),
  });
}

interface RegistrationResponse {
  user: Lead;
  count_inputs?: unknown;
}

export async function fetchLead(
  idPassbook: string,
  leadId: string,
  passbook?: Passbook | null,
): Promise<Lead | null> {
  try {
    const headers = passbookHeaders(idPassbook);
    const logOpts = normalizeOptsFromPassbook(passbook);
    const res = await legacy.get<RegistrationResponse>(`/registration/${leadId}`, headers);
    const lead = res.user ? normalizeLeadUniqueIdField(res.user) : null;
    if (!lead) return null;

    const balance =
      lead.total_points ??
      deriveLeadBalanceFromRegistration(lead.logs, res.count_inputs, passbook ?? undefined);

    if (lead.serialNumber) {
      try {
        const logs = await legacy.get<unknown[]>(`/log/${lead.serialNumber}`, headers);
        lead.logs = normalizePointLogs(logs, logOpts);
      } catch {
        lead.logs = normalizePointLogs(lead.logs, logOpts);
      }
    } else {
      lead.logs = normalizePointLogs(lead.logs, logOpts);
    }

    if (balance != null) {
      lead.total_points = balance;
    }

    return lead;
  } catch {
    return null;
  }
}

export async function fetchIndividualMessages(
  idPassbook: string,
  uniqueId: string,
): Promise<IndividualMessage[]> {
  try {
    const res = await legacy.get<IndividualMessage[]>(
      `/messages-individuals/${idPassbook}/${encodeURIComponent(uniqueId)}`,
      passbookHeaders(idPassbook),
    );
    return Array.isArray(res) ? res : [];
  } catch {
    return [];
  }
}

export interface SendIndividualMessagePayload {
  serialNumber: string;
  uniqueId: string;
  email?: string;
  phone?: string;
  description: string;
  current_stamps?: number | string;
  birthday?: string;
  gender?: string;
}

export async function sendIndividualMessage(idPassbook: string, payload: SendIndividualMessagePayload) {
  await legacy.post(
    '/registration/message',
    {
      id_passbook: idPassbook,
      email: payload.email,
      description: payload.description,
      serialNumber: payload.serialNumber,
      uniqueId: payload.uniqueId,
      current_stamps: String(payload.current_stamps ?? 0),
      phone: payload.phone,
      birthday: payload.birthday,
      gender: payload.gender,
    },
    passbookHeaders(idPassbook),
  );
}

/** Campo interno do lead — não dispara push nem grava em messages-individuals. */
export async function saveLeadObservation(idPassbook: string, leadId: string, observacao: string) {
  await legacy.put(`/user/${leadId}/observacao`, { observacao }, passbookHeaders(idPassbook));
}

/** Limite para agregar KPIs a partir da listagem (mesma fonte dos cards de lead). */
const MAX_LEADS_FOR_STATS_AGGREGATION = 5_000;

function passbookHeaders(idPassbook: string) {
  return { headers: { 'X-Passbook-Id': idPassbook } };
}

function statsFromLeads(leads: Lead[], total: number): LeadsStats {
  const installed = leads.filter(isCardInstalled).length;
  return {
    total,
    installed,
    notInstalled: Math.max(0, total - installed),
    ios: leads.filter(isIosDevice).length,
    android: leads.filter(isAndroidDevice).length,
  };
}

async function platformCount(idPassbook: string, typeAccess: string): Promise<number> {
  const res = await legacy.get<{ platform?: number }>(
    `/platform_user/${idPassbook}/${encodeURIComponent(typeAccess)}`,
    passbookHeaders(idPassbook),
  );
  return res.platform ?? 0;
}

async function fetchLeadsStatsFromPlatform(idPassbook: string, total: number): Promise<LeadsStats> {
  const [ios, iosApp, android, androidApp] = await Promise.all([
    platformCount(idPassbook, 'iOS'),
    platformCount(idPassbook, 'APP-ios'),
    platformCount(idPassbook, 'Android'),
    platformCount(idPassbook, 'APP-android'),
  ]);
  const iosTotal = ios + iosApp;
  const androidTotal = android + androidApp;
  const installed = iosTotal + androidTotal;

  return {
    total,
    installed,
    notInstalled: Math.max(0, total - installed),
    ios: iosTotal,
    android: androidTotal,
  };
}

export async function fetchLeadsStats(
  idPassbook: string,
  passbook: Passbook,
  scopeUser?: LeadsScopeUser | null,
): Promise<LeadsStats> {
  const multi = isMultiStoreLeads(passbook);
  const endpoint = multi ? '/usersPaginateMulti' : '/usersPaginate';
  const headers = passbookHeaders(idPassbook);
  const user = multi ? scopeUserPayload(scopeUser) : undefined;

  const baseBody = {
    id_passbook: idPassbook,
    role: ['Member', 'Employee'],
    skip: 0,
    search: { value: '' },
    order: [{ column: 0, dir: 'desc' as const }],
    columns: LEAD_COLUMNS,
    ...(multi ? { multiStore: true } : {}),
    ...(user ? { user } : {}),
  };

  const head = await legacy.post<PaginatedResponse<Lead>>(endpoint, { ...baseBody, length: 1 }, headers);
  const total = head.recordsFiltered ?? 0;

  if (total === 0) {
    return { total: 0, installed: 0, notInstalled: 0, ios: 0, android: 0 };
  }

  if (total <= MAX_LEADS_FOR_STATS_AGGREGATION) {
    const page = await legacy.post<PaginatedResponse<Lead>>(endpoint, { ...baseBody, length: total }, headers);
    return statsFromLeads(page.data ?? [], total);
  }

  return fetchLeadsStatsFromPlatform(idPassbook, total);
}
